import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { Calendar, Clock, Users, Search, Plus, Edit, Trash2, MapPin, Phone, Mail, X, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { enhancedSearch } from '../utils/searchUtils';
import { LocalStorageManager } from '../utils/localStorage';
import { addToRecycleBin } from '../utils/recycleBin';
import { useNotifications } from '../contexts/NotificationContext';
import { StatusBadge, appointmentStatusOptions, candidateStageOptions } from '../components/ui/StatusBadge';
import { subscribeToAppointments, addAppointment as addAppointmentToFirebase, updateAppointment as updateAppointmentInFirebase, deleteAppointment as deleteAppointmentFromFirebase, type Appointment as FirebaseAppointment } from '../services/appointmentService';

type Appointment = Omit<FirebaseAppointment, 'status'> & {
  status: FirebaseAppointment['status'] | 'No Show';
  candidateEmail?: string;
  candidatePhone?: string;
  appointmentType?: string;
  candidateStage?: 'Interviewing' | 'Awaiting Visa' | 'Cancelled' | 'Deployed' | 'On Hold';
};

const AppointmentsPage: React.FC = () => {
  const { notifyBadgeChange, notifyContentChange } = useNotifications();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to Firebase appointments
  useEffect(() => {
    console.log('📅 Appointments: Setting up Firebase subscription...');
    setLoading(true);
    
    // Clear any old localStorage appointments to prevent duplicates
    const localStorageKey = 'recruitment_crm_appointments';
    const localAppointments = localStorage.getItem(localStorageKey);
    if (localAppointments) {
      console.log('🧹 Clearing old localStorage appointments to prevent duplicates');
      localStorage.removeItem(localStorageKey);
    }
    
    const unsubscribe = subscribeToAppointments((firebaseAppointments) => {
      console.log('📅 Appointments: Received', firebaseAppointments.length, 'appointments from Firebase');
      setAppointments(firebaseAppointments);
      setLoading(false);
    });

    return () => {
      console.log('📅 Appointments: Cleaning up Firebase subscription...');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedAppointments, setSelectedAppointments] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [formData, setFormData] = useState({
    candidateName: '',
    candidateEmail: '',
    candidatePhone: '',
    appointmentType: '',
    date: '',
    time: '',
    location: '',
    interviewer: '',
    status: 'Scheduled',
    candidateStage: 'Interviewing',
    notes: ''
  });
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: '',
    location: '',
    phone: '',
    reason: ''
  });

  const appointmentTypes = [
    'Initial Interview',
    'Technical Interview',
    'Final Interview',
    'Medical Examination',
    'Document Verification',
    'Contract Signing',
    'Pre-deployment Briefing',
    'Follow-up Meeting'
  ];

  const locations = [
    'Naim Investments Office - Room A',
    'Naim Investments Office - Room B',
    'Naim Investments Office - Conference Room',
    'Nairobi Medical Center',
    'Virtual Meeting (Zoom)',
    'Virtual Meeting (Google Meet)',
    'Client Office',
    'Other'
  ];

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const handleSelectAppointment = (appointmentId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedAppointments(prev => [...prev, appointmentId]);
    } else {
      setSelectedAppointments(prev => prev.filter(id => id !== appointmentId));
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedAppointments(
        filteredAppointments
          .map((appointment) => appointment.id)
          .filter((id): id is string => Boolean(id))
      );
    } else {
      setSelectedAppointments([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedAppointments.length} appointment(s)? They will be moved to the recycle bin.`)) {
      try {
        // Get appointment names before deletion for notifications
        const appointmentsToDelete = appointments.filter(
          (appointment) => appointment.id && selectedAppointments.includes(appointment.id)
        );
        console.log('🗑️ Bulk deleting appointments:', appointmentsToDelete.map(a => ({ id: a.id, candidateName: a.candidateName })));
        console.log('📅 Appointments before bulk deletion:', appointments.length);
        
        let deletedCount = 0;
        let failedCount = 0;
        
        // Process each selected appointment
        for (const appointmentId of selectedAppointments) {
          try {
            const appointmentToDelete = appointments.find(a => a.id === appointmentId);
            
            if (appointmentToDelete) {
              // Delete from Firebase (which is the source of truth)
              await deleteAppointmentFromFirebase(appointmentId);
              console.log(`🔥 Deleted appointment ${appointmentId} from Firebase`);
              
              // Add to recycle bin
              addToRecycleBin({
                type: 'appointment',
                name: `${appointmentToDelete.candidateName} - ${appointmentToDelete.appointmentType}`,
                details: `${appointmentToDelete.appointmentType} appointment on ${appointmentToDelete.date} at ${appointmentToDelete.time}`,
                deletedBy: 'Current User',
                data: appointmentToDelete
              });
              
              deletedCount++;
              // Notify content change for successfully deleted appointment
              notifyContentChange('Appointments Page', 'deleted', 'Appointment', appointmentToDelete.candidateName);
            } else {
              failedCount++;
              console.error(`❌ Appointment ${appointmentId} not found for deletion`);
            }
          } catch (error) {
            failedCount++;
            console.error(`❌ Error processing appointment ${appointmentId}:`, error);
          }
        }
        setSelectedAppointments([]);
        
        // Show appropriate success/error message
        if (failedCount > 0) {
          alert(`${deletedCount} appointment(s) moved to recycle bin successfully. ${failedCount} failed to delete.`);
        } else {
          alert(`${deletedCount} appointment(s) moved to recycle bin successfully!`);
        }
      } catch (error) {
        console.error('❌ Error during bulk delete operation:', error);
        alert('An error occurred while deleting appointments. Please try again.');
      }
    }
  };

  const filteredAppointments = useMemo(() => {
    let filtered = appointments;

    if (searchTerm) {
      filtered = filtered.filter(appointment => 
        enhancedSearch(searchTerm, [
          appointment.candidateName || '',
          appointment.candidateEmail || '',
          appointment.candidatePhone || '',
          appointment.appointmentType || '',
          appointment.location || '',
          appointment.interviewer || '',
          appointment.notes || '',
          appointment.candidateStage || ''
        ])
      );
    }

    if (dateFilter) {
      filtered = filtered.filter(appointment => appointment.date === dateFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(appointment => appointment.status === statusFilter);
    }

    if (stageFilter !== 'all') {
      filtered = filtered.filter(appointment => appointment.candidateStage === stageFilter);
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortField as keyof Appointment];
        const bValue = b[sortField as keyof Appointment];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
          return sortDirection === 'asc' ? comparison : -comparison;
        }
        
        return 0;
      });
    } else {
      // Default sort by date and time
      filtered = filtered.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });
    }

    return filtered;
  }, [appointments, searchTerm, dateFilter, statusFilter, stageFilter, sortField, sortDirection]);

  const resetForm = () => {
    setFormData({
      candidateName: '',
      candidateEmail: '',
      candidatePhone: '',
      appointmentType: '',
      date: '',
      time: '',
      location: '',
      interviewer: '',
      status: 'Scheduled',
      candidateStage: 'Interviewing',
      notes: ''
    });
  };

  const resetRescheduleForm = () => {
    setRescheduleData({
      date: '',
      time: '',
      location: '',
      phone: '',
      reason: ''
    });
  };

  const handleAddAppointment = async () => {
    try {
      const appointmentData = {
        ...formData,
        status: formData.status as Appointment['status'],
        candidateStage: formData.candidateStage as Appointment['candidateStage'],
        createdAt: new Date().toISOString().split('T')[0]
      };

      // Add to Firebase
      const newAppointmentId = await addAppointmentToFirebase(appointmentData as any);
      console.log('🔥 Added appointment to Firebase:', newAppointmentId);
      
      // Notify content change
      notifyContentChange('Appointments Page', 'added', 'Appointment', formData.candidateName);
      
      setShowAddModal(false);
      resetForm();
      alert('Appointment scheduled successfully!');
    } catch (error) {
      console.error('Failed to add appointment:', error);
      alert('Failed to schedule appointment. Please try again.');
    }
  };

  const handleEditAppointment = async () => {
    if (!selectedAppointment?.id) return;

    try {
      const updatedAppointment = {
        ...formData,
        status: formData.status as Appointment['status'],
        candidateStage: formData.candidateStage as Appointment['candidateStage']
      };

      // Check for badge changes
      if (selectedAppointment.status !== updatedAppointment.status) {
        notifyBadgeChange('Appointments Page', 'Appointment Status', selectedAppointment.status, updatedAppointment.status);
      }
      if (selectedAppointment.candidateStage !== updatedAppointment.candidateStage) {
        notifyBadgeChange('Appointments Page', 'Candidate Stage', selectedAppointment.candidateStage, updatedAppointment.candidateStage);
      }

      // Update in Firebase
      await updateAppointmentInFirebase(selectedAppointment.id, updatedAppointment as any);
      console.log('🔥 Updated appointment in Firebase:', selectedAppointment.id);
      
      setShowEditModal(false);
      setSelectedAppointment(null);
      resetForm();
      alert('Appointment updated successfully!');
    } catch (error) {
      console.error('Failed to update appointment:', error);
      alert('Failed to update appointment. Please try again.');
    }
  };

  const handleRescheduleAppointment = async () => {
    if (!selectedAppointment?.id) return;

    try {
      const updatedAppointment = {
        date: rescheduleData.date,
        time: rescheduleData.time,
        location: rescheduleData.location,
        status: 'Rescheduled' as Appointment['status'],
        notes: `${selectedAppointment.notes}\n\nRescheduled: ${rescheduleData.reason} (Contact: ${rescheduleData.phone})`
      };

      // Update in Firebase
      await updateAppointmentInFirebase(selectedAppointment.id, updatedAppointment as any);
      console.log('🔥 Rescheduled appointment in Firebase:', selectedAppointment.id);
      
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      resetRescheduleForm();
      alert('Appointment rescheduled successfully!');
    } catch (error) {
      console.error('Failed to reschedule appointment:', error);
      alert('Failed to reschedule appointment. Please try again.');
    }
  };

  const handleDeleteAppointment = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this appointment? It will be moved to the recycle bin.')) {
      try {
        const appointmentToDelete = appointments.find(a => a.id === id);
        console.log('🗑️ Attempting to delete appointment:', appointmentToDelete);
        console.log('📅 Appointments before deletion:', appointments.length);
        
        if (!appointmentToDelete) {
          console.error('❌ Appointment not found for deletion');
          alert('Appointment not found.');
          return;
        }
        
        // Delete from Firebase (which is the source of truth)
        await deleteAppointmentFromFirebase(id);
        console.log('🔥 Deleted appointment from Firebase:', id);
        
        // Add to recycle bin
        addToRecycleBin({
          type: 'appointment',
          name: `${appointmentToDelete.candidateName} - ${appointmentToDelete.appointmentType}`,
          details: `${appointmentToDelete.appointmentType} appointment on ${appointmentToDelete.date} at ${appointmentToDelete.time}`,
          deletedBy: 'Current User',
          data: appointmentToDelete
        });
        
        // Notify content change
        notifyContentChange('Appointments Page', 'deleted', 'Appointment', appointmentToDelete.candidateName);
        
        alert('Appointment moved to recycle bin successfully!');
      } catch (error) {
        console.error('❌ Error deleting appointment:', error);
        alert('An error occurred while deleting the appointment.');
      }
    }
  };

  const openEditModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      candidateName: appointment.candidateName || '',
      candidateEmail: appointment.candidateEmail || '',
      candidatePhone: appointment.candidatePhone || '',
      appointmentType: appointment.appointmentType || appointment.type || '',
      date: appointment.date,
      time: appointment.time,
      location: appointment.location || '',
      interviewer: appointment.interviewer || '',
      status: appointment.status,
      candidateStage: appointment.candidateStage || 'Interviewing',
      notes: appointment.notes || ''
    });
    setShowEditModal(true);
  };

  const openRescheduleModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.date,
      time: appointment.time,
      location: appointment.location || '',
      phone: appointment.candidatePhone || '',
      reason: ''
    });
    setShowRescheduleModal(true);
  };

  const handleAppointmentStatusChange = async (appointmentId: string, newStatus: string) => {
    const appointment = appointments.find(a => a.id === appointmentId);
    if (!appointment) return;

    try {
      const oldStatus = appointment.status;
      
      // Convert from StatusBadge format (lowercase-hyphen) to display format
      const displayStatus = newStatus.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      // Update in Firebase
      await updateAppointmentInFirebase(appointmentId, { status: displayStatus as any } as any);
      console.log('🔥 Updated appointment status in Firebase:', appointmentId);

      // Notify badge change for cross-page sync
      notifyBadgeChange('Appointments Page', `${appointment.candidateName} Status`, oldStatus, displayStatus);
      
      // Dispatch event for cross-page sync
      window.dispatchEvent(new CustomEvent('appointmentStatusChanged', { 
        detail: { id: appointmentId, oldStatus, newStatus: displayStatus, candidateName: appointment.candidateName }
      }));

      console.log(`📊 Updated appointment ${appointment.candidateName} status: ${oldStatus} → ${displayStatus}`);
    } catch (error) {
      console.error('Failed to update appointment status:', error);
      alert('Failed to update appointment status. Please try again.');
    }
  };

  const handleCandidateStageChange = async (appointmentId: string, newStage: string) => {
    const appointment = appointments.find(a => a.id === appointmentId);
    if (!appointment) return;

    try {
      const oldStage = appointment.candidateStage;
      
      // Convert from StatusBadge format (lowercase-hyphen) to display format
      const displayStage = newStage.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      // Update in Firebase
      await updateAppointmentInFirebase(appointmentId, { candidateStage: displayStage as any } as any);
      console.log('🔥 Updated appointment stage in Firebase:', appointmentId);

      // Notify badge change for cross-page sync
      notifyBadgeChange('Appointments Page', `${appointment.candidateName} Stage`, oldStage, displayStage);
      
      // Dispatch event for cross-page sync
      window.dispatchEvent(new CustomEvent('candidateStageChanged', { 
        detail: { id: appointmentId, oldStatus: oldStage, newStatus: displayStage, candidateName: appointment.candidateName }
      }));

      console.log(`📊 Updated appointment ${appointment.candidateName} stage: ${oldStage} → ${displayStage}`);
    } catch (error) {
      console.error('Failed to update appointment stage:', error);
      alert('Failed to update appointment stage. Please try again.');
    }
  };

  const todayAppointments = appointments.filter(
    a => a.date === new Date().toISOString().split('T')[0] && a.status === 'Scheduled'
  ).length;

  const upcomingAppointments = appointments.filter(
    a => a.date > new Date().toISOString().split('T')[0] && a.status === 'Scheduled'
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>
            Appointments
          </h1>
          <p className="text-gray-600 mt-1">
            Schedule and manage candidate interviews and meetings
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={recruitmentTheme.animations.scaleIn}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Appointments</p>
                <p className="text-2xl font-bold text-[#8b6914]">{todayAppointments}</p>
              </div>
              <Calendar className="w-8 h-8 text-[#8b6914]" />
            </div>
          </CardContent>
        </Card>
        <Card className={recruitmentTheme.animations.scaleIn}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Upcoming</p>
                <p className="text-2xl font-bold text-blue-600">{upcomingAppointments}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className={recruitmentTheme.animations.scaleIn}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {appointments.filter(a => a.status === 'Completed').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className={recruitmentTheme.animations.scaleIn}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">No Shows</p>
                <p className="text-2xl font-bold text-orange-600">
                  {appointments.filter(a => a.status === 'No Show').length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Appointments List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-[#8b6914]" />
              <CardTitle className="text-[#8b6914]">All Appointments</CardTitle>
              <button 
                onClick={() => setShowAddModal(true)}
                data-add
                className="bg-white hover:bg-gray-50 text-[#8b6914] border-2 border-[#f0e4cc] rounded-xl px-6 py-3 font-semibold transition-all duration-200 flex items-center space-x-2 ml-4"
              >
                <Plus className="w-4 h-4" />
                <span>Schedule Appointment</span>
              </button>
              <div className="relative ml-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search appointments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${recruitmentTheme.components.input} pl-10 w-80`}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className={recruitmentTheme.components.input}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={recruitmentTheme.components.select}
              >
                <option value="all">All Status</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
                <option value="No Show">No Show</option>
                <option value="Rescheduled">Rescheduled</option>
              </select>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className={recruitmentTheme.components.select}
              >
                <option value="all">All Stages</option>
                <option value="Interviewing">Interviewing</option>
                <option value="Awaiting Visa">Awaiting Visa</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Deployed">Deployed</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions */}
          {selectedAppointments.length > 0 && (
            <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm text-blue-700">
                {selectedAppointments.length} appointment(s) selected
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDeleteSelected}
                  className={`${recruitmentTheme.components.button.secondary} text-red-600 hover:text-red-700 flex items-center space-x-1`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Selected</span>
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {filteredAppointments.map((appointment, index) => (
              <div
                key={appointment.id || `${appointment.candidateName}-${appointment.date}-${index}`}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-start space-x-3 flex-1">
                    <input
                      type="checkbox"
                      checked={appointment.id ? selectedAppointments.includes(appointment.id) : false}
                      onChange={(e) => {
                        if (appointment.id) {
                          handleSelectAppointment(appointment.id, e.target.checked);
                        }
                      }}
                      className="rounded mt-1"
                    />
                    <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center mt-1">
                      {index + 1}.
                    </span>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-[#8b6914]">{appointment.candidateName}</h3>
                          <p className="text-sm text-gray-600">{appointment.appointmentType}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <StatusBadge
                            currentStatus={appointment.candidateStage?.toLowerCase().replace(/\s+/g, '-') || 'interviewing'}
                            options={candidateStageOptions}
                            onStatusChange={(newStage) => {
                              if (appointment.id) {
                                handleCandidateStageChange(appointment.id, newStage);
                              }
                            }}
                            size="sm"
                          />
                          <StatusBadge
                            currentStatus={appointment.status?.toLowerCase().replace(/\s+/g, '-') || 'scheduled'}
                            options={appointmentStatusOptions}
                            onStatusChange={(newStatus) => {
                              if (appointment.id) {
                                handleAppointmentStatusChange(appointment.id, newStatus);
                              }
                            }}
                            size="sm"
                          />
                          {appointment.status === 'Rescheduled' && (
                            <button
                              onClick={() => openRescheduleModal(appointment)}
                              className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700 transition-colors"
                            >
                              Reschedule Again
                            </button>
                          )}
                        </div>
                      </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(appointment.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{appointment.time}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{appointment.location}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{appointment.interviewer}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        <a 
                          href={`mailto:${appointment.candidateEmail}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {appointment.candidateEmail}
                        </a>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        <a 
                          href={`tel:${appointment.candidatePhone}`}
                          className="text-green-600 hover:text-green-800 hover:underline"
                        >
                          {appointment.candidatePhone}
                        </a>
                      </div>
                    </div>

                    {appointment.notes && (
                      <p className="text-sm text-gray-500 mt-3 italic">
                        Note: {appointment.notes}
                      </p>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => openEditModal(appointment)}
                      className={recruitmentTheme.components.button.ghost}
                      title="Edit appointment"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (appointment.id) {
                          handleDeleteAppointment(appointment.id);
                        }
                      }}
                      className={`${recruitmentTheme.components.button.ghost} text-red-600 hover:text-red-700`}
                      title="Delete appointment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredAppointments.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-[#8b6914] opacity-50" />
              <h3 className="mt-2 text-sm font-medium text-[#8b6914]">No appointments found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || dateFilter || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by scheduling your first appointment.'
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Appointment Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">
                {showAddModal ? 'Schedule New Appointment' : 'Edit Appointment'}
              </h2>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setSelectedAppointment(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Candidate Name *</label>
                <input
                  type="text"
                  value={formData.candidateName}
                  onChange={(e) => setFormData({...formData, candidateName: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Candidate Email *</label>
                <input
                  type="email"
                  value={formData.candidateEmail}
                  onChange={(e) => setFormData({...formData, candidateEmail: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Candidate Phone *</label>
                <input
                  type="tel"
                  value={formData.candidatePhone}
                  onChange={(e) => setFormData({...formData, candidatePhone: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Appointment Type *</label>
                <select
                  value={formData.appointmentType}
                  onChange={(e) => setFormData({...formData, appointmentType: e.target.value})}
                  className={recruitmentTheme.components.select}
                  required
                >
                  <option value="">Select type</option>
                  {appointmentTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Time *</label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Location *</label>
                <select
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className={recruitmentTheme.components.select}
                  required
                >
                  <option value="">Select location</option>
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Interviewer *</label>
                <input
                  type="text"
                  value={formData.interviewer}
                  onChange={(e) => setFormData({...formData, interviewer: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Name of interviewer"
                  required
                />
              </div>
              
              {showEditModal && (
                <div>
                  <label className="block text-sm font-medium text-[#8b6914] mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => {
                      setFormData({...formData, status: e.target.value});
                      if (e.target.value === 'Rescheduled') {
                        setShowEditModal(false);
                        openRescheduleModal(selectedAppointment!);
                      }
                    }}
                    className={recruitmentTheme.components.select}
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="No Show">No Show</option>
                    <option value="Rescheduled">Rescheduled</option>
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Candidate Stage</label>
                <select
                  value={formData.candidateStage}
                  onChange={(e) => setFormData({...formData, candidateStage: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="Interviewing">Interviewing</option>
                  <option value="Awaiting Visa">Awaiting Visa</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Deployed">Deployed</option>
                  <option value="On Hold">On Hold</option>
                </select>
              </div>
              
              <div className={showEditModal ? '' : 'md:col-span-2'}>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className={`${recruitmentTheme.components.textarea} h-20`}
                  placeholder="Additional notes about the appointment..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setSelectedAppointment(null);
                  resetForm();
                }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={showAddModal ? handleAddAppointment : handleEditAppointment}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
                disabled={
                  !formData.candidateName || !formData.candidateEmail || 
                  !formData.candidatePhone || !formData.appointmentType || 
                  !formData.date || !formData.time || !formData.location || 
                  !formData.interviewer
                }
              >
                <Save className="w-4 h-4" />
                <span>{showAddModal ? 'Schedule' : 'Update'} Appointment</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Appointment Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Reschedule Appointment</h2>
              <button 
                onClick={() => {
                  setShowRescheduleModal(false);
                  setSelectedAppointment(null);
                  resetRescheduleForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">New Date *</label>
                <input
                  type="date"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({...rescheduleData, date: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">New Time *</label>
                <input
                  type="time"
                  value={rescheduleData.time}
                  onChange={(e) => setRescheduleData({...rescheduleData, time: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">New Location *</label>
                <select
                  value={rescheduleData.location}
                  onChange={(e) => setRescheduleData({...rescheduleData, location: e.target.value})}
                  className={recruitmentTheme.components.select}
                  required
                >
                  <option value="">Select location</option>
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Contact Phone *</label>
                <input
                  type="tel"
                  value={rescheduleData.phone}
                  onChange={(e) => setRescheduleData({...rescheduleData, phone: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Phone number for contact"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Reason for Rescheduling *</label>
                <textarea
                  value={rescheduleData.reason}
                  onChange={(e) => setRescheduleData({...rescheduleData, reason: e.target.value})}
                  className={`${recruitmentTheme.components.textarea} h-20`}
                  placeholder="Please explain why this appointment needs to be rescheduled..."
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => {
                  setShowRescheduleModal(false);
                  setSelectedAppointment(null);
                  resetRescheduleForm();
                }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={handleRescheduleAppointment}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
                disabled={
                  !rescheduleData.date || !rescheduleData.time || 
                  !rescheduleData.location || !rescheduleData.phone || 
                  !rescheduleData.reason
                }
              >
                <Save className="w-4 h-4" />
                <span>Reschedule</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
